import React, { useState, useEffect } from 'react';
import {
    ConnectWebSocketForOverlay,
    SetSocketMessageHandler,
    ClearSocketMessageHandler,
    DisconnectWebSocket
} from '../../utilities/BL/Zones';

import { AnecdotesTicker } from './Overlays/AnecdotesTicker';
import { AnecdotesFullScreen } from './Overlays/AnecdotesFullScreen';

/**
 * COMPONENTE MAESTRO: ANECDOTES OVERLAY
 * ------------------------------------------------------------------
 * "Cerebro" del Overlay Híbrido (Cintillo + Pantalla Completa).
 * Responsabilidades:
 * 1. Forzar 100% fondo transparente en body, html y root para broadcast (OBS/vMix).
 * 2. Conectar a WebSocket con el canal 'anecdote' (`ConnectWebSocketForOverlay('anecdote', ...)`).
 * 3. Escuchar eventos tipo 'anecdote' (show, hide, set_mode, config).
 * 4. Gestionar el modo activo ('fullscreen' vs 'cintillo') y la posición vertical (offsetY).
 * 5. Renderizado condicional de los componentes visuales hijos.
 * ------------------------------------------------------------------
 */
export const Anecdotes = () => {
    // Modo de visualización: 'fullscreen' (default) o 'cintillo'
    const [displayMode, setDisplayMode] = useState('fullscreen');
    // Posición vertical dinámica para modo cintillo
    const [offsetY, setOffsetY] = useState(0);
    // Payload actual para el componente visual hijo
    const [anecdoteData, setAnecdoteData] = useState(null);
    // Estado de la conexión WebSocket
    const [connectionStatus, setConnectionStatus] = useState('idle');

    // -------------------------------------------------------------------------
    // 1. EFECTO: FORZAR FONDO 100% TRANSPARENTE EN OBS / VMIX
    // -------------------------------------------------------------------------
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const rootElement = document.getElementById('root');
        const originalRootBg = rootElement ? rootElement.style.backgroundColor : '';

        // Forzar transparencia completa
        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            if (rootElement) {
                if (originalRootBg) {
                    rootElement.style.backgroundColor = originalRootBg;
                } else {
                    rootElement.style.removeProperty('background-color');
                }
            }
        };
    }, []);

    // -------------------------------------------------------------------------
    // 2. CICLO DE VIDA: CONEXIÓN WEBSOCKET Y ESCUCHA DE EVENTOS
    // -------------------------------------------------------------------------
    useEffect(() => {
        const handleIncomingMessage = (message) => {
            if (!message || typeof message !== 'object' || !message.type) return;

            const msgType = String(message.type).toLowerCase();
            if (msgType !== 'anecdote') return;

            const payload = (message.data && typeof message.data === 'object') ? message.data : message;
            const action = typeof payload.action === 'string' ? payload.action.toLowerCase() : '';

            // A. Cambio de modo de visualización en caliente
            if (action === 'set_mode' || payload.displayMode) {
                const requestedMode = String(payload.displayMode || '').toLowerCase();
                const cleanMode = requestedMode === 'cintillo' ? 'cintillo' : 'fullscreen';
                setDisplayMode(cleanMode);
            }

            // B. Ajuste de posición vertical (offsetY) para cintillo
            if ((action === 'config' || payload.offsetY !== undefined) && typeof payload.offsetY === 'number' && Number.isFinite(payload.offsetY)) {
                const safeOffset = Math.min(1000, Math.max(-1000, payload.offsetY));
                setOffsetY(safeOffset);
            }

            // C. Entrega del payload al componente visual
            setAnecdoteData(payload);
        };

        SetSocketMessageHandler(handleIncomingMessage);

        ConnectWebSocketForOverlay(
            'anecdote',
            (status) => setConnectionStatus(status),
            (msg) => console.log('[Overlay Anecdotes] Notificación:', msg)
        );

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, []);

    return (
        <div
            className={`anecdotes-overlay-master anecdotes-overlay-master--${displayMode}`}
            data-testid="anecdotes-overlay-master"
        >
            {displayMode === 'cintillo' ? (
                <AnecdotesTicker data={anecdoteData} offsetY={offsetY} />
            ) : (
                <AnecdotesFullScreen data={anecdoteData} />
            )}

            {/* Debug discreto en esquina inferior derecha para técnicos de cabina en caso de error */}
            {connectionStatus === 'error' && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: 4,
                        right: 4,
                        background: 'rgba(220, 53, 69, 0.85)',
                        color: '#fff',
                        padding: '2px 6px',
                        fontSize: '10px',
                        fontFamily: 'monospace',
                        borderRadius: '3px',
                        zIndex: 9999
                    }}
                    title="Error de conexión WebSocket con el servidor"
                >
                    WS Error
                </div>
            )}
        </div>
    );
};

export default Anecdotes;
